import React from 'react';
import { Row, Col } from 'reactstrap';
import { Field, FieldArray } from 'formik';
import { useTenant } from '../../../../hooks/useTenant';
import { storage } from '../../../../services/firebase';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { toast } from 'react-toastify';
import { getRankImage } from '../../../../models/gamification';
import { NeoInput, NeoButton } from '../../../../components/Common';

const GamificationLevelRanks = ({ values, setFieldValue }) => {
    const [editingIndex, setEditingIndex] = React.useState(null);
    const [isUploading, setIsUploading] = React.useState({});
    const { idTenant } = useTenant();

    const handleImageUpload = async (e, index, rankId) => {
        const file = e.target.files[0];
        if (!file) return;

        if (!file.type.startsWith('image/')) {
            toast.error('Por favor, selecione uma imagem válida.');
            return;
        }

        if (file.size > 2 * 1024 * 1024) { // 2MB limit
            toast.error('A imagem deve ter no máximo 2MB.');
            return;
        }

        setIsUploading(prev => ({ ...prev, [index]: true }));

        try {
            const fileExtension = file.name.split('.').pop();
            const fileName = `${rankId}_${Date.now()}.${fileExtension}`;
            const storageRef = ref(storage, `tenants/${idTenant}/gamification/levelRanks/${fileName}`);
            
            await uploadBytes(storageRef, file);
            const downloadURL = await getDownloadURL(storageRef);

            setFieldValue(`levelRanks.${index}.imageId`, downloadURL);
            toast.success('Imagem atualizada com sucesso!');
        } catch (error) {
            console.error("Erro ao fazer upload:", error);
            toast.error('Erro ao fazer upload da imagem.');
        } finally {
            setIsUploading(prev => ({ ...prev, [index]: false }));
        }
    };

    return (
        <div className="p-2">
            <div className="mb-4 d-flex align-items-center">
                <div className="neo-section-icon-circle flex-shrink-0">
                    <i className="mdi mdi-trending-up font-size-18 text-accent" />
                </div>
                <div className="ms-3">
                    <h5 className="mb-0 fw-bold text-dark neo-font-outfit font-size-15">Ranking de Níveis (Level Ranks)</h5>
                    <p className="text-muted mb-0 font-size-12">Configure os nomes e a pontuação mínima (XP) necessária para atingir cada nível.</p>
                </div>
            </div>
            
            <FieldArray name="levelRanks">
                {({ push, remove }) => (
                    <div>
                        {values.levelRanks && values.levelRanks.length > 0 ? (
                            <Row className="g-4">
                                {values.levelRanks.map((rank, index) => (
                                    <Col xl={3} lg={4} md={6} sm={12} key={rank.id || index}>
                                        {editingIndex === index ? (
                                            <div className="neo-gamification-card neo-gamification-card--editing text-start h-100">
                                                {index > 0 && (
                                                    <button 
                                                        type="button"
                                                        className="neo-btn-circle-delete"
                                                        onClick={() => {
                                                            remove(index);
                                                            setEditingIndex(null);
                                                        }}
                                                    >
                                                        <i className="mdi mdi-trash-can-outline font-size-14"></i>
                                                    </button>
                                                )}

                                                <Field name={`levelRanks.${index}.name`}>
                                                    {({ field, meta }) => (
                                                        <NeoInput
                                                            {...field}
                                                            label="Nome do Nível *"
                                                            placeholder="Ex: Bronze"
                                                            error={meta.touched && meta.error ? meta.error : ''}
                                                            className="mb-2"
                                                            size="sm"
                                                        />
                                                    )}
                                                </Field>

                                                <Field name={`levelRanks.${index}.minXp`}>
                                                    {({ field, meta }) => (
                                                        <NeoInput
                                                            {...field}
                                                            type="number"
                                                            label="XP Mínimo *"
                                                            placeholder="0"
                                                            error={meta.touched && meta.error ? meta.error : ''}
                                                            className="mb-2"
                                                            size="sm"
                                                            readOnly={index === 0}
                                                        />
                                                    )}
                                                </Field>
                                                {index === 0 && (
                                                    <small className="text-muted font-size-10 d-block mb-2">Sempre inicia com 0 XP.</small>
                                                )}

                                                <div className="mb-3">
                                                    <label className="form-label font-size-11 text-muted text-uppercase fw-bold mb-1">
                                                        Upload de Imagem
                                                    </label>
                                                    <input 
                                                        type="file" 
                                                        accept="image/*"
                                                        className="form-control form-control-sm bg-light border-0 py-1"
                                                        disabled={isUploading[index]}
                                                        onChange={(e) => handleImageUpload(e, index, rank.id || `rank_${index}`)}
                                                    />
                                                    {isUploading[index] && (
                                                        <small className="text-accent d-block mt-1">
                                                            <i className="mdi mdi-loading mdi-spin me-1"></i>Enviando...
                                                        </small>
                                                    )}
                                                </div>

                                                <NeoButton 
                                                    variant="primary" 
                                                    size="sm" 
                                                    className="w-100 fw-bold"
                                                    onClick={() => setEditingIndex(null)}
                                                >
                                                    OK
                                                </NeoButton>
                                            </div>
                                        ) : (
                                            <div className="neo-gamification-card text-center h-100">
                                                <button 
                                                    type="button"
                                                    className="neo-btn-circle-edit"
                                                    onClick={() => setEditingIndex(index)}
                                                >
                                                    <i className="mdi mdi-pencil font-size-14"></i>
                                                </button>

                                                <div className="neo-badge-floating">
                                                    {rank.minXp} XP
                                                </div>
                                                
                                                <div className="mt-3 mb-2">
                                                    <div className="badge-image-container">
                                                        {getRankImage(rank.imageId || rank.id) ? (
                                                            <img 
                                                                src={getRankImage(rank.imageId || rank.id)} 
                                                                alt={rank.id} 
                                                            />
                                                        ) : (
                                                            <i className="mdi mdi-shield-star text-warning font-size-48"></i>
                                                        )}
                                                    </div>
                                                </div>

                                                <h6 className="fw-bold text-dark mb-1 font-size-13 neo-font-outfit">{rank.name}</h6>
                                            </div>
                                        )}
                                    </Col>
                                ))}
                            </Row>
                        ) : (
                            <div className="text-center p-5 bg-light-subtle d-flex flex-column align-items-center justify-content-center border border-2 border-dashed rounded-3" style={{ minHeight: "180px" }}>
                                <i className="mdi mdi-shield-off font-size-36 text-muted mb-2 opacity-50"></i>
                                <h6 className="text-muted fw-semibold mb-0">Nenhum nível configurado no momento.</h6>
                            </div>
                        )}

                        <div className="text-center mt-4">
                            <NeoButton 
                                type="button" 
                                variant="outline-primary"
                                className="px-5 fw-bold shadow-sm"
                                icon="mdi-plus-circle-outline"
                                onClick={() => {
                                    const nextIndex = values.levelRanks ? values.levelRanks.length : 0;
                                    const nextId = nextIndex + 1;
                                    const lastXp = nextIndex > 0 ? Number(values.levelRanks[nextIndex - 1].minXp) || 0 : 0;
                                    const nextXp = lastXp + 100;

                                    push({
                                        id: `rank_${Date.now()}`,
                                        name: `Nível ${nextId}`,
                                        minXp: nextIndex === 0 ? 0 : nextXp,
                                        imageId: `rank_${nextId <= 20 ? nextId : 20}`
                                    });
                                    setEditingIndex(nextIndex);
                                }}
                            >
                                Adicionar Novo Nível
                            </NeoButton>
                        </div>
                    </div>
                )}
            </FieldArray>
        </div>
    );
};

export default GamificationLevelRanks;
